'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface HeroSearchSectionProps {
  onSearch: (query: string) => void;
  onVoiceSearch: () => void;
}

const HeroSearchSection = ({ onSearch, onVoiceSearch }: HeroSearchSectionProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isListening, setIsListening] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onSearch(searchQuery);
    }
  };

  const handleVoiceClick = () => {
    setIsListening(true);
    onVoiceSearch();
    setTimeout(() => setIsListening(false), 3000);
  };

  return (
    <div className="bg-gradient-to-br from-primary via-primary to-secondary py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary-foreground text-center mb-3">
          Fresh Fish, Delivered Daily
        </h1>
        <p className="text-primary-foreground/90 text-center text-lg mb-8">
          Quality seafood from trusted sources, straight to your doorstep
        </p>

        <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
          <div className="relative flex items-center">
            <Icon 
              name="MagnifyingGlassIcon" 
              size={20} 
              className="absolute left-4 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Search for fish, prawns, crabs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-32 py-4 rounded-lg border-2 border-transparent focus:border-accent focus-ring text-foreground"
            />
            <div className="absolute right-2 flex items-center gap-2">
              <button
                type="button"
                onClick={handleVoiceClick}
                className={`p-3 rounded-md transition-smooth ${
                  isListening
                    ? 'bg-error text-error-foreground animate-pulse-subtle'
                    : 'bg-muted hover:bg-border'
                }`}
                aria-label="Voice search"
              >
                <Icon name="MicrophoneIcon" size={20} />
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-accent text-accent-foreground rounded-md font-medium transition-smooth hover:shadow-elevation-md hover-lift"
              >
                Search
              </button>
            </div>
          </div>
        </form>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
          <span className="text-primary-foreground/80 text-sm">Popular:</span>
          {['Salmon', 'Prawns', 'Tuna', 'Pomfret', 'Crab'].map((item) => (
            <button
              key={item}
              onClick={() => {
                setSearchQuery(item);
                onSearch(item);
              }}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-primary-foreground rounded-full text-sm font-medium transition-smooth"
            >
              {item}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeroSearchSection;